import React, { useEffect, useState, useCallback } from 'react';
import { Edit, Plus, RefreshCw, Trash2, Search, Filter, ArrowRightCircle, CheckCircle, X, ChevronLeft, ChevronRight, Info, MessageSquare, Clock, Shield, Building2 } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

const DemandesMobilite = () => {
  const { filialeId } = useFiliale();
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [dropdownData, setDropdownData] = useState({
    personnel: [],
    filiales: [],
    titres: []
  });
  const [formData, setFormData] = useState({
    personnel_id: '',
    filiale_source_id: filialeId || '',
    filiale_destination_id: '',
    titre_destination_id: '',
    motivation: '',
    disponibilite: '',
    statut: 'en_attente'
  });
  const [filters, setFilters] = useState({
    personnel_id: '',
    statut: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState(1);

  const fetchDemandes = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filialeId && filialeId !== 'all') {
        params.filiale_id = filialeId;
      }
      if (filters.personnel_id) {
        params.personnel_id = filters.personnel_id;
      }
      if (filters.statut) {
        params.statut = filters.statut;
      }

      const queryString = new URLSearchParams(params).toString();
      const response = await http.get(`/api/demandes-mobilite${queryString ? '?' + queryString : ''}`);
      setDemandes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur chargement demandes:', error);
    } finally {
      setLoading(false);
    }
  }, [filialeId, filters.personnel_id, filters.statut]);

  const fetchDropdownData = useCallback(async () => {
    try {
      const currentFilialeId = filialeId;
      const personnelUrl = (currentFilialeId && currentFilialeId !== 'all')
        ? `/api/personnel?filiale_id=${currentFilialeId}`
        : '/api/personnel';

      const [personnel, filiales, titres] = await Promise.all([
        http.get(personnelUrl),
        http.get('/api/filiales?all=true'), // ?all=true pour voir toutes les filiales (mobilité)
        http.get('/api/titres', { params: currentFilialeId && currentFilialeId !== 'all' ? { filiale_id: currentFilialeId } : {} })
      ]);

      const personnelArray = Array.isArray(personnel.data) ? personnel.data :
                             (personnel.data && personnel.data.data && Array.isArray(personnel.data.data) ? personnel.data.data : []);

      const titresArray = Array.isArray(titres.data) ? titres.data :
                        (titres.data && titres.data.data && Array.isArray(titres.data.data) ? titres.data.data : []);

      setDropdownData({
        personnel: personnelArray,
        filiales: Array.isArray(filiales.data) ? filiales.data : [],
        titres: titresArray
      });
    } catch (error) {
      console.error('Erreur chargement dropdown:', error);
    }
  }, [filialeId]);

  useEffect(() => {
    fetchDemandes();
    fetchDropdownData();
  }, [fetchDemandes, fetchDropdownData]);

  const handleCreate = () => {
    setEditingItem(null);
    setActiveTab(1);
    setFormData({
      personnel_id: '',
      filiale_source_id: filialeId || '',
      filiale_destination_id: '',
      titre_destination_id: '',
      motivation: '',
      disponibilite: '',
      statut: 'en_attente'
    });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setActiveTab(1);
    setFormData({ ...item });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette demande de mobilité ?')) {
      return;
    }
    try {
      await http.delete(`/api/demandes-mobilite/${id}`);
      fetchDemandes();
    } catch (error) {
      console.error('Erreur suppression demande:', error);
      alert('Erreur lors de la suppression de la demande');
    }
  };

  const handleValider = async (id, role) => {
    const commentaire = prompt('Commentaire de validation (optionnel):');
    // Pour RH destination, permettre d'accepter ou refuser
    let statut = role === 'rh_source' ? 'valide_rh_source' : 'valide_rh_destination';
    
    if (role === 'rh_destination') {
      const choix = window.confirm('Voulez-vous ACCEPTER ou REFUSER cette demande ?\n\nOK = ACCEPTER\nAnnuler = REFUSER');
      statut = choix ? 'accepte' : 'refuse';
    }
    
    try {
      await http.put(`/api/demandes-mobilite/${id}/valider/${role}`, { 
        commentaire,
        statut
      });
      fetchDemandes();
    } catch (error) {
      console.error('Erreur validation:', error);
      alert('Erreur lors de la validation');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await http.put(`/api/demandes-mobilite/${editingItem.id}`, formData);
      } else {
        await http.post('/api/demandes-mobilite', formData);
      }
      setShowModal(false);
      fetchDemandes();
    } catch (error) {
      console.error('Erreur sauvegarde demande:', error);
      alert('Erreur lors de la sauvegarde de la demande');
    }
  };

  // Filtrage et pagination
  const filteredDemandes = demandes.filter(d => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (d.prenom && d.prenom.toLowerCase().includes(searchLower)) ||
      (d.nom && d.nom.toLowerCase().includes(searchLower)) ||
      (d.titre_destination_nom_developpe && d.titre_destination_nom_developpe.toLowerCase().includes(searchLower))
    );
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDemandes = filteredDemandes.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredDemandes.length / itemsPerPage);

  const getStatutColor = (statut) => {
    const colors = {
      'en_attente': 'bg-gray-100 text-gray-800',
      'en_cours': 'bg-blue-100 text-blue-800',
      'valide_manager': 'bg-yellow-100 text-yellow-800',
      'valide_rh_source': 'bg-orange-100 text-orange-800',
      'valide_rh_destination': 'bg-purple-100 text-purple-800',
      'valide_direction': 'bg-indigo-100 text-indigo-800',
      'accepte': 'bg-green-100 text-green-800',
      'refuse': 'bg-red-100 text-red-800',
      'annule': 'bg-gray-300 text-gray-800'
    };
    return colors[statut] || 'bg-gray-100 text-gray-800';
  };

  const getStatutIcon = (statut) => {
    const icons = {
      'en_attente': <Clock className="h-4 w-4" />,
      'en_cours': <Clock className="h-4 w-4" />,
      'valide_manager': <CheckCircle className="h-4 w-4" />,
      'valide_rh_source': <CheckCircle className="h-4 w-4" />,
      'valide_rh_destination': <CheckCircle className="h-4 w-4" />,
      'valide_direction': <CheckCircle className="h-4 w-4" />,
      'accepte': <CheckCircle className="h-4 w-4" />,
      'refuse': <X className="h-4 w-4" />,
      'annule': <X className="h-4 w-4" />
    };
    return icons[statut] || <Clock className="h-4 w-4" />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Demandes de Mobilité"
        description="Gestion des demandes de mobilité inter-filiale"
        icon={ArrowRightCircle}
        actions={[
          {
            label: 'Nouvelle demande',
            icon: Plus,
            onClick: handleCreate,
            primary: true
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: fetchDemandes,
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8">
        {/* Filtres et recherche */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-gray-500" />
              <h3 className="font-semibold text-gray-700">Filtres</h3>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-64"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employé</label>
              <select
                value={filters.personnel_id}
                onChange={(e) => setFilters({ ...filters, personnel_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">Tous</option>
                {dropdownData.personnel.map(p => (
                  <option key={p.idpersonnel} value={p.idpersonnel}>{p.prenom} {p.nom}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
              <select
                value={filters.statut}
                onChange={(e) => setFilters({ ...filters, statut: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">Tous</option>
                <option value="en_attente">En attente</option>
                <option value="en_cours">En cours</option>
                <option value="valide_manager">Validé manager</option>
                <option value="valide_rh_source">Validé RH source</option>
                <option value="valide_rh_destination">Validé RH destination</option>
                <option value="valide_direction">Validé direction</option>
                <option value="accepte">Accepté</option>
                <option value="refuse">Refusé</option>
                <option value="annule">Annulé</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tableau des demandes */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex h-48 items-center justify-center text-sm text-gray-500">
              Chargement...
            </div>
          ) : currentDemandes.length === 0 ? (
            <div className="px-4 py-16 text-center text-sm text-gray-500">
              Aucune demande de mobilité trouvée.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Employé</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Filiale source</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Filiale destination</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Poste souhaité</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date demande</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Statut</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentDemandes.map((demande) => (
                      <tr key={demande.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {demande.prenom} {demande.nom}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {demande.filiale_source_nom}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {demande.filiale_destination_nom}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {demande.titre_destination_nom_developpe || demande.titre_destination_nom || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {new Date(demande.date_demande).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatutColor(demande.statut)}`}>
                            {getStatutIcon(demande.statut)}
                            {demande.statut.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(demande)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleValider(demande.id, 'rh_source')}
                              className="text-purple-600 hover:text-purple-800"
                              title="Valider RH source"
                            >
                              <Shield className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleValider(demande.id, 'rh_destination')}
                              className="text-orange-600 hover:text-orange-800"
                              title="Valider RH destination"
                            >
                              <Building2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(demande.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredDemandes.length)} sur {filteredDemandes.length} demandes
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Précédent
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 border rounded ${
                        currentPage === page
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Modal Demande de Mobilité */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="w-full max-w-4xl rounded-xl bg-white shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 border-b bg-gray-50">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">
                    {editingItem ? 'Modifier' : 'Créer'} Demande de Mobilité
                  </h3>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-gray-500 hover:text-gray-900"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Onglets */}
              <div className="border-b bg-white">
                <div className="flex">
                  <button
                    key="tab-1"
                    onClick={() => setActiveTab(1)}
                    className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                      activeTab === 1
                        ? 'border-blue-600 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Détails
                  </button>
                  <button
                    key="tab-2"
                    onClick={() => setActiveTab(2)}
                    className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                      activeTab === 2
                        ? 'border-blue-600 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Motivation
                  </button>
                </div>
              </div>

              {/* Contenu */}
              <div className="flex-1 overflow-y-auto p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Onglet 1: Détails */}
                  {activeTab === 1 && (
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                        <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-blue-800">
                          <p className="font-semibold mb-1">Détails de la demande</p>
                          <p>Sélectionnez l'employé, les filiales source et destination, ainsi que le poste souhaité. La demande suivra un workflow de validation multi-étapes.</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Employé *</label>
                          <select
                            value={formData.personnel_id}
                            onChange={(e) => setFormData({ ...formData, personnel_id: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                          >
                            <option value="">Sélectionner</option>
                            {dropdownData.personnel.map(p => (
                              <option key={p.idpersonnel} value={p.idpersonnel}>{p.prenom} {p.nom}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Filiale source *</label>
                          <select
                            value={formData.filiale_source_id}
                            onChange={(e) => setFormData({ ...formData, filiale_source_id: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                          >
                            <option value="">Sélectionner</option>
                            {dropdownData.filiales.map(f => (
                              <option key={f.idfiliale} value={f.idfiliale}>{f.libelle}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Filiale destination *</label>
                          <select
                            value={formData.filiale_destination_id}
                            onChange={(e) => setFormData({ ...formData, filiale_destination_id: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                          >
                            <option value="">Sélectionner</option>
                            {dropdownData.filiales.map(f => (
                              <option key={f.idfiliale} value={f.idfiliale}>{f.libelle}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Poste souhaité</label>
                          <select
                            value={formData.titre_destination_id}
                            onChange={(e) => setFormData({ ...formData, titre_destination_id: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          >
                            <option value="">Sélectionner</option>
                            {dropdownData.titres.map(t => (
                              <option key={t.idtitre} value={t.idtitre}>{t.nom_developpe || t.nom}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Date de disponibilité</label>
                          <input
                            type="date"
                            value={formData.disponibilite}
                            onChange={(e) => setFormData({ ...formData, disponibilite: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Onglet 2: Motivation */}
                  {activeTab === 2 && (
                    <div className="space-y-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                        <Info className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-green-800">
                          <p className="font-semibold mb-1">Motivation de la demande</p>
                          <p>Expliquez clairement les raisons de cette demande de mobilité. Cette information sera prise en compte lors du processus de validation.</p>
                        </div>
                      </div>
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                          <MessageSquare className="h-4 w-4 text-blue-600" />
                          Motivation *
                        </label>
                        <textarea
                          value={formData.motivation}
                          onChange={(e) => setFormData({ ...formData, motivation: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          rows="6"
                          placeholder="Expliquez les raisons de votre demande de mobilité, vos objectifs professionnels, les opportunités que vous voyez..."
                          required
                        />
                      </div>
                    </div>
                  )}
                </form>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveTab(Math.max(1, activeTab - 1))}
                    disabled={activeTab === 1}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Précédent
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab(Math.min(2, activeTab + 1))}
                    disabled={activeTab === 2}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    Suivant
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editingItem ? 'Modifier' : 'Créer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemandesMobilite;
